/**
 * The app reporting its own errors (ADR-0141).
 *
 * Installed once at app start. An unhandled error, an unhandled promise
 * rejection, a screen that crashed, or an API call the server failed becomes a
 * support report without the person doing anything. The server dedupes by
 * fingerprint, so one bad screen is one ticket with a count.
 *
 * Reporting is fire and forget. Nothing here awaits for a caller, throws into
 * one, writes to the console (that would report itself), or runs in the
 * scorebook's event path: it is scheduled after the error has been handled, in
 * its own try/catch. A device with no connection queues the report (at most
 * ten) and sends it when it is back.
 */

import { ids, network } from '@good-season/platform';
import {
  SUPPORT_AUTO_MESSAGE_MAX,
  SUPPORT_AUTO_STACK_MAX,
  type SupportAutoErrorAccepted,
  type SupportAutoErrorRequest,
  type SupportAutoSource,
} from '@good-season/schema';
import { diagnosticFor } from '@good-season/ui';

import { api, ApiError, OfflineError } from '../api/client.js';
import { onApiFailure, type ApiFailure } from '../api/failure-hook.js';
import { enqueueAuto, pendingAuto, removeAuto } from './auto-outbox.js';
import { collectDiagnostics, scrubLine } from './diagnostics.js';
import { isLiveScorebookPath } from './live-scorebook.js';
import { deviceClass, viewportLabel, WEB_APP_VERSION } from './report-context.js';

/** The Help feature's diagnostic prefix (docs/SUPPORT_CODES.md). */
const SURFACE = 'HP';

const API_ROOT = import.meta.env.BASE_URL.replace(/\/$/, '');

/** The same error on the same screen is reported once in this long. */
const REPEAT_MS = 10 * 60 * 1000;
/** Most reports one page load makes. A loop of errors must not become a loop of requests. */
const SESSION_CAP = 6;

export interface AutoNotice {
  /** The ticket's reference, or, for a failure the API reported itself, its diagnostic code. */
  readonly reference: string;
  readonly followupToken: string;
  readonly occurrences: number;
  /** True for a failure the API reported itself: the token alone finds the ticket. */
  readonly viaToken?: boolean;
}

export interface AutoErrorInput {
  readonly source: SupportAutoSource;
  readonly error?: unknown;
  readonly name?: string;
  readonly message?: string;
  readonly stack?: string;
  readonly apiStatus?: number;
  readonly apiPath?: string;
  readonly diagnosticCode?: string;
  /** False for a report the person has no reason to hear about. */
  readonly notify?: boolean;
}

const recent = new Map<string, number>();
let sentThisSession = 0;
/** Notices for a failure the API filed itself. Counted apart: they send nothing from here. */
let noticesThisSession = 0;
let teardown: (() => void) | null = null;
let current: AutoNotice | null = null;
const listeners = new Set<(notice: AutoNotice | null) => void>();

function emit(next: AutoNotice | null): void {
  current = next;
  for (const listener of listeners) listener(next);
}

/** The open notice, now and when it changes. The listener hears the current one at once. */
export function subscribeAutoNotice(listener: (notice: AutoNotice | null) => void): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissAutoNotice(): void {
  emit(null);
}

function describe(error: unknown): { name: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message,
      ...(typeof error.stack === 'string' ? { stack: error.stack } : {}),
    };
  }
  if (typeof error === 'string') return { name: 'Error', message: error };
  // A DOMException is not an Error in every environment, but it has a name and a message.
  if (typeof error === 'object' && error !== null) {
    const { name, message, stack } = error as Record<string, unknown>;
    if (typeof name === 'string' && typeof message === 'string') {
      return { name, message, ...(typeof stack === 'string' ? { stack } : {}) };
    }
  }
  let message = 'a value that is not an Error was thrown';
  try {
    message = JSON.stringify(error) ?? message;
  } catch {
    // Keep the generic message.
  }
  return { name: 'Error', message };
}

/** Errors that are noise or that another path already tells the story of. */
function ignored(name: string, message: string, error: unknown): boolean {
  if (error instanceof OfflineError) return true;
  // An API failure is reported where it happens, with its path and status.
  if (error instanceof ApiError) return true;
  if (name === 'AbortError') return true;
  if (/^ResizeObserver loop/.test(message)) return true;
  if (message === 'Script error.' || message === 'Script error') return true;
  // A screen or file that failed to load: the network or a deploy, not a bug in the app.
  if (
    /dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError/i.test(
      message,
    )
  ) {
    return true;
  }
  if (/^(Failed to fetch|Load failed|NetworkError when attempting to fetch)/i.test(message)) {
    return true;
  }
  return false;
}

function trimStack(stack: string | undefined): string | undefined {
  if (stack === undefined) return undefined;
  const text = stack
    .split('\n')
    .slice(0, 10)
    .map((line) => scrubLine(line))
    .join('\n');
  return text.slice(0, SUPPORT_AUTO_STACK_MAX);
}

function build(input: AutoErrorInput): SupportAutoErrorRequest | null {
  const described = input.error === undefined ? null : describe(input.error);
  const name = (input.name ?? described?.name ?? 'Error').slice(0, 80);
  const raw = input.message ?? described?.message ?? '';
  if (ignored(name, raw, input.error)) return null;
  const message = scrubLine(raw).slice(0, SUPPORT_AUTO_MESSAGE_MAX);
  const stack = trimStack(input.stack ?? described?.stack);
  const route = window.location.pathname || '/';
  const code = input.diagnosticCode;
  return {
    clientId: ids.uuid(),
    source: input.source,
    name: name.length === 0 ? 'Error' : name,
    message,
    ...(stack === undefined || stack === '' ? {} : { stack }),
    route: route.slice(0, 300),
    device: deviceClass(window.innerWidth) as SupportAutoErrorRequest['device'],
    appVersion: WEB_APP_VERSION,
    viewport: viewportLabel(window.innerWidth, window.innerHeight),
    diagnostics: collectDiagnostics(),
    ...(code !== undefined && /^[A-Z0-9-]{4,12}$/.test(code) ? { diagnosticCode: code } : {}),
    ...(input.apiStatus === undefined ? {} : { apiStatus: input.apiStatus }),
    ...(input.apiPath === undefined
      ? {}
      : { apiPath: input.apiPath.replace(/[?#].*$/, '').slice(0, 120) }),
  };
}

function key(report: SupportAutoErrorRequest): string {
  return [report.name, report.message.replace(/\d+/g, '#'), report.apiPath ?? report.route].join(
    '|',
  );
}

/**
 * One request. `accepted` is the filed report; `retry` is true when the server was busy or
 * limiting this network, so a queued report should stay. Throws when no answer arrived.
 */
async function post(
  report: SupportAutoErrorRequest,
): Promise<{ accepted: SupportAutoErrorAccepted | null; retry: boolean }> {
  const response = await fetch(`${API_ROOT}/api/support-reports/auto`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    signal: AbortSignal.timeout(15_000),
  });
  if (response.status === 429 || response.status >= 500) return { accepted: null, retry: true };
  if (response.status !== 201) return { accepted: null, retry: false };
  const body = (await response.json()) as Partial<SupportAutoErrorAccepted>;
  if (
    typeof body.reference !== 'string' ||
    typeof body.followupToken !== 'string' ||
    typeof body.occurrences !== 'number'
  ) {
    return { accepted: null, retry: false };
  }
  return { accepted: body as SupportAutoErrorAccepted, retry: false };
}

/** A report kept while the device was online (busy server, no answer) is tried again after this. */
const RETRY_FIRST_MS = 60 * 1000;
const RETRY_MAX_MS = 15 * 60 * 1000;
let retryDelay = RETRY_FIRST_MS;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** One pending retry at most, backing off, so a server that stays down is asked less and less. */
function scheduleRetry(): void {
  if (retryTimer !== null) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    // Offline: the reconnect listener sends them when the device is back.
    if (network.isOnline()) void flushQueued().catch(() => undefined);
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
}

async function send(report: SupportAutoErrorRequest, notify: boolean): Promise<void> {
  if (!network.isOnline()) {
    await enqueueAuto(report, Date.now());
    return;
  }
  let accepted: SupportAutoErrorAccepted | null;
  try {
    const result = await post(report);
    // Told to slow down: keep it for later rather than lose it.
    if (result.retry) {
      await enqueueAuto(report, Date.now());
      scheduleRetry();
      return;
    }
    accepted = result.accepted;
  } catch {
    // No answer: keep it for when there is one.
    await enqueueAuto(report, Date.now());
    scheduleRetry();
    return;
  }
  if (accepted === null || !notify || current !== null) return;
  // The scorebook is never interrupted. The report is already filed.
  if (isLiveScorebookPath(window.location.pathname)) return;
  emit(accepted);
}

/** Sends what was queued while the device was offline. Quiet: nobody is waiting on these. */
async function flushQueued(): Promise<void> {
  for (const report of await pendingAuto()) {
    try {
      const result = await post(report);
      // Busy or limited: stop here and try again later.
      if (result.retry) {
        scheduleRetry();
        return;
      }
      await removeAuto(report.clientId);
    } catch {
      scheduleRetry();
      return;
    }
  }
  // Everything went through: the next trouble starts from the short delay again.
  retryDelay = RETRY_FIRST_MS;
}

export function reportAutoError(input: AutoErrorInput): void {
  try {
    const report = build(input);
    if (report === null) return;
    const id = key(report);
    const now = Date.now();
    const last = recent.get(id);
    if (last !== undefined && now - last < REPEAT_MS) return;
    if (sentThisSession >= SESSION_CAP) return;
    recent.set(id, now);
    sentThisSession += 1;
    const notify = input.notify !== false && input.source !== 'network';
    // After the current task, so the error that caused this has finished being handled.
    setTimeout(() => {
      void send(report, notify).catch(() => undefined);
    }, 0);
  } catch {
    // Reporting must never be the thing that fails.
  }
}

/** For an ErrorBoundary's `onError`: a screen that crashed, with the code its fallback shows. */
export function reportBoundary(surface: string): (error: unknown) => void {
  return (error) => {
    reportAutoError({ source: 'boundary', error, diagnosticCode: diagnosticFor(error, surface) });
  };
}

function noticeForAnsweredFault(failure: Extract<ApiFailure, { kind: 'server' }>): void {
  try {
    const token = failure.followup;
    if (token === undefined || !/^[A-Za-z0-9_-]{32,64}$/.test(token)) return;
    if (!/^[A-Z0-9]{6}$/.test(failure.diagnostic)) return;
    const id = `answered|${failure.path.replace(/[?#].*$/, '')}|${String(failure.status)}`;
    const now = Date.now();
    const last = recent.get(id);
    if (last !== undefined && now - last < REPEAT_MS) return;
    // The scorebook is never interrupted, and says nothing that would use up the quota of
    // what a later error may need to say.
    if (isLiveScorebookPath(window.location.pathname)) return;
    if (noticesThisSession >= SESSION_CAP || current !== null) return;
    recent.set(id, now);
    noticesThisSession += 1;
    emit({ reference: failure.diagnostic, followupToken: token, occurrences: 1, viaToken: true });
  } catch {
    // Telling the person must never be the thing that fails.
  }
}

function onApiFailureReport(failure: ApiFailure): void {
  // The reporting route failing must not report itself.
  if (failure.path.startsWith('/api/support-reports')) return;
  // A call that got no answer is flaky connectivity or a deploy restart, not a bug report:
  // never filed, however many screens saw it.
  if (failure.kind === 'network') return;
  // The API answered and filed its own report for this 5xx. Nothing is filed from here (that would
  // be a second ticket for one failure); the person is told, with the code, and the token the API
  // sent back lets them add what they were doing to that ticket.
  if (failure.answered) {
    noticeForAnsweredFault(failure);
    return;
  }
  reportAutoError({
    source: 'api',
    name: 'ApiError',
    message: `${failure.code} (${String(failure.status)})`,
    apiStatus: failure.status,
    apiPath: failure.path,
    diagnosticCode: failure.diagnostic,
  });
}

export function installAutoReporting(): void {
  if (teardown !== null || typeof window === 'undefined') return;
  const onError = (event: ErrorEvent): void => {
    reportAutoError({
      source: 'window',
      ...(event.error === undefined || event.error === null
        ? { name: 'Error', message: event.message }
        : { error: event.error as unknown }),
    });
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    reportAutoError({ source: 'rejection', error: event.reason as unknown });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  onApiFailure(onApiFailureReport);
  const stopNetwork = network.onChange((online) => {
    if (online) void flushQueued().catch(() => undefined);
  });
  teardown = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    stopNetwork();
  };
  void flushQueued().catch(() => undefined);
}

/** Tests start from nothing seen and nothing installed. */
export function resetAutoReportingForTests(): void {
  recent.clear();
  sentThisSession = 0;
  noticesThisSession = 0;
  current = null;
  listeners.clear();
  teardown?.();
  teardown = null;
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  retryDelay = RETRY_FIRST_MS;
  onApiFailure(null);
}

export type AddDetailsResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly offline: boolean; readonly diagnostic: string };

/** Adds the person's own words to the ticket. The token returned at filing is the proof. */
/**
 * A failure the API filed itself is filed a moment after its reply goes out (and later still if the
 * database was down), so a note sent at once can arrive before the ticket does. A not-found answer
 * for such a note is tried again a few times before it is called a failure.
 */
export const VIA_TOKEN_RETRY_MS = 2000;
const VIA_TOKEN_TRIES = 4;

export async function addAutoDetails(
  notice: AutoNotice,
  note: string,
  retryMs: number = VIA_TOKEN_RETRY_MS,
): Promise<AddDetailsResult> {
  const viaToken = notice.viaToken === true;
  for (let attempt = 1; ; attempt += 1) {
    try {
      await api(
        viaToken
          ? '/api/support-reports/details'
          : `/api/support-reports/${notice.reference}/details`,
        { method: 'POST', body: JSON.stringify({ token: notice.followupToken, note }) },
        { timeoutMs: 15_000 },
      );
      return { ok: true };
    } catch (error) {
      if (
        viaToken &&
        attempt < VIA_TOKEN_TRIES &&
        error instanceof ApiError &&
        error.status === 404
      ) {
        await new Promise((resolve) => setTimeout(resolve, retryMs));
        continue;
      }
      return {
        ok: false,
        offline: error instanceof OfflineError,
        diagnostic: error instanceof ApiError ? error.diagnostic : diagnosticFor(error, SURFACE),
      };
    }
  }
}
